import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * Sprint 34D — LLM Cost Governance widget (admin only).
 * Renders month-to-date spend, day spend, cap %, tier, and inline cap editor.
 *
 * Props:
 *   cost: {month_spend_usd, day_spend_usd, cap_usd, pct_of_cap, tier, kill_switch_active}
 *   onCapUpdate: async (newCap: number) => void
 */
const TIER_STYLES = {
  green:  { border: "border-emerald-500", bar: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-800", label: "Óptimo" },
  yellow: { border: "border-yellow-500", bar: "bg-yellow-500", chip: "bg-yellow-100 text-yellow-800", label: "Vigilar" },
  orange: { border: "border-orange-500", bar: "bg-orange-500", chip: "bg-orange-100 text-orange-800", label: "Atención" },
  red:    { border: "border-red-500",    bar: "bg-red-500",    chip: "bg-red-100 text-red-800",    label: "Crítico" },
  kill:   { border: "border-red-700",    bar: "bg-red-700",    chip: "bg-red-200 text-red-900",    label: "Kill-switch activo" },
};

export default function LlmCostWidget({ cost, onCapUpdate }) {
  const [editing, setEditing] = useState(false);
  const [newCap, setNewCap] = useState(cost.cap_usd);
  const style = TIER_STYLES[cost.tier] || TIER_STYLES.green;
  const pct = Math.min(100, Math.max(0, cost.pct_of_cap || 0));

  return (
    <div
      className={`rounded-lg border-2 ${style.border} bg-card p-4 mb-4`}
      data-testid="llm-cost-widget"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Consumo LLM · <span className="text-primary">Solo admin</span>
          </div>
          <Badge className={style.chip} data-testid="llm-tier-badge">{style.label}</Badge>
          {cost.kill_switch_active && (
            <Badge className="bg-red-600 text-white animate-pulse" data-testid="llm-killswitch-badge">
              Modo emergencia activo
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs">
          {!editing ? (
            <>
              <span className="text-muted-foreground">Cap mensual:</span>
              <span className="font-semibold">US$ {cost.cap_usd.toFixed(0)}</span>
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="llm-cap-edit-btn">
                Editar
              </Button>
            </>
          ) : (
            <>
              <span className="text-muted-foreground">Nuevo cap USD:</span>
              <input
                type="number"
                value={newCap}
                onChange={(e) => setNewCap(Number(e.target.value))}
                className="w-24 px-2 py-1 border border-border rounded text-sm"
                data-testid="llm-cap-input"
              />
              <Button
                size="sm"
                onClick={() => { onCapUpdate(newCap); setEditing(false); }}
                data-testid="llm-cap-save-btn"
              >
                Guardar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setNewCap(cost.cap_usd); }}>
                Cancelar
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-3 bg-muted rounded-full overflow-hidden mb-3" data-testid="llm-progress">
        <div
          className={`h-full ${style.bar} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Row of metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <MetricCell label="Mes a la fecha" value={`US$ ${cost.month_spend_usd.toFixed(2)}`} testid="llm-mtd" />
        <MetricCell label="Hoy" value={`US$ ${cost.day_spend_usd.toFixed(2)}`} testid="llm-today" />
        <MetricCell label="% del cap" value={`${pct.toFixed(1)}%`} testid="llm-pct" />
        <MetricCell
          label="Estado"
          value={cost.kill_switch_active ? "Templates + humano" : "Operando"}
          testid="llm-state"
        />
      </div>

      <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
        A 50% se avisa · A 80% se alerta a IT · A 95% se activa el kill-switch automático
        (templates + escalación humana). El cap se puede ajustar en cualquier momento.
      </p>
    </div>
  );
}

function MetricCell({ label, value, testid }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-base font-semibold">{value}</span>
    </div>
  );
}
